import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { GOAL_TEXT, goalStatus } from "@/lib/goal-status";
import { cn } from "@/lib/utils";

interface LeadsByChannelResponse {
  months: string[];
  monthsElapsed: number;
  fiscalYearLabel: string;
  priorYearLabel: string;
  priorYearTotals: number[];
  channels: string[];
  groups: { label: string; channels: readonly string[] }[];
  counts: Record<string, Record<string, number>>;
  goals: Record<string, number>;
}

function formatMonth(month: string) {
  const [year = 0, m = 1] = month.split("-").map(Number);
  return new Date(year, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

export function LeadsByChannelCard() {
  const [data, setData] = useState<LeadsByChannelResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch("/api/marketing/leads-by-channel")
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return null;

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Leads Generated — Faithful Platform</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  const {
    months,
    monthsElapsed,
    fiscalYearLabel,
    priorYearLabel,
    priorYearTotals,
    channels,
    groups,
    counts,
    goals,
  } = data;

  const monthTotals = months.map((month) =>
    channels.reduce((sum, channel) => sum + (counts[channel]?.[month] ?? 0), 0),
  );
  const channelTotals: Record<string, number> = {};
  for (const channel of channels) {
    channelTotals[channel] = months.reduce(
      (sum, month) => sum + (counts[channel]?.[month] ?? 0),
      0,
    );
  }
  const grandTotal = monthTotals.reduce((a, b) => a + b, 0);
  const goalTotal = channels.reduce((sum, channel) => sum + (goals[channel] ?? 0), 0);
  const projectedTotals: Record<string, number> = {};
  for (const channel of channels) {
    projectedTotals[channel] = Math.round(((channelTotals[channel] ?? 0) / monthsElapsed) * 12);
  }
  const grandProjected = channels.reduce(
    (sum, channel) => sum + (projectedTotals[channel] ?? 0),
    0,
  );
  const priorYearTotal = priorYearTotals.reduce((a, b) => a + b, 0);
  const priorYearToDateTotal = priorYearTotals.slice(0, monthsElapsed).reduce((a, b) => a + b, 0);
  const yoyChange =
    priorYearToDateTotal > 0 ? (grandTotal - priorYearToDateTotal) / priorYearToDateTotal : 0;

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => {
        const groupChannels = group.channels;
        const isDigitalGroup = group.label === "Digital Lead Channels";
        const groupMonthTotals = months.map((month) =>
          groupChannels.reduce((sum, channel) => sum + (counts[channel]?.[month] ?? 0), 0),
        );
        const groupTotal = groupMonthTotals.reduce((a, b) => a + b, 0);
        const groupGoalTotal = groupChannels.reduce(
          (sum, channel) => sum + (goals[channel] ?? 0),
          0,
        );
        const groupProjectedTotal = groupChannels.reduce(
          (sum, channel) => sum + (projectedTotals[channel] ?? 0),
          0,
        );
        const monthlyGoals: Record<string, number> = {};
        for (const channel of groupChannels) {
          monthlyGoals[channel] = (goals[channel] ?? 0) / 12;
        }
        const groupMonthlyGoalTotal = groupChannels.reduce(
          (sum, channel) => sum + (monthlyGoals[channel] ?? 0),
          0,
        );

        return (
          <Card key={group.label}>
            <CardHeader>
              <CardTitle>
                {group.label} ({fiscalYearLabel})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium text-muted-foreground sticky left-0 bg-card">
                        Channel
                      </th>
                      {months.map((month) => (
                        <th
                          key={month}
                          className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap"
                        >
                          {formatMonth(month)}
                        </th>
                      ))}
                      <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">
                        Total
                      </th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                        Goal
                      </th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                        Remaining
                      </th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                        Projected
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupChannels.map((channel) => {
                      const total = channelTotals[channel] ?? 0;
                      const goal = goals[channel] ?? 0;
                      const remaining = Math.max(0, goal - total);
                      const projected = projectedTotals[channel] ?? 0;
                      const monthlyGoal = monthlyGoals[channel] ?? 0;
                      return (
                        <>
                          <tr
                            key={channel}
                            className={cn(isDigitalGroup ? "border-0" : "border-b last:border-0")}
                          >
                            <td className="py-2 pr-4 whitespace-nowrap sticky left-0 bg-card">
                              {channel}
                            </td>
                            {months.map((month) => {
                              const actual = counts[channel]?.[month] ?? 0;
                              return (
                                <td
                                  key={month}
                                  className={cn(
                                    "text-right py-2 px-2 tabular-nums",
                                    isDigitalGroup && GOAL_TEXT[goalStatus(actual, monthlyGoal)],
                                  )}
                                >
                                  {actual}
                                </td>
                              );
                            })}
                            <td className="text-right py-2 px-2 font-semibold tabular-nums">
                              {total}
                            </td>
                            <td className="text-right py-2 px-2 tabular-nums text-muted-foreground">
                              {goal}
                            </td>
                            <td className="text-right py-2 px-2 tabular-nums text-muted-foreground">
                              {remaining}
                            </td>
                            <td
                              className={cn(
                                "text-right py-2 px-2 tabular-nums",
                                GOAL_TEXT[goalStatus(projected, goal)],
                              )}
                            >
                              {projected}
                            </td>
                          </tr>
                          {isDigitalGroup && (
                            <tr key={`${channel}-goal`} className="border-b last:border-0">
                              <td className="py-1 pr-4 pl-4 whitespace-nowrap sticky left-0 bg-card text-xs text-muted-foreground">
                                Monthly Goal
                              </td>
                              {months.map((month) => (
                                <td
                                  key={month}
                                  className="text-right py-1 px-2 tabular-nums text-xs text-muted-foreground"
                                >
                                  {monthlyGoal.toFixed(1)}
                                </td>
                              ))}
                              <td colSpan={4} />
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 font-semibold">
                      <td className="py-2 pr-4 sticky left-0 bg-card">Section Total</td>
                      {groupMonthTotals.map((total, i) => (
                        <td key={months[i]} className="text-right py-2 px-2 tabular-nums">
                          {total}
                        </td>
                      ))}
                      <td className="text-right py-2 px-2 tabular-nums">{groupTotal}</td>
                      <td className="text-right py-2 px-2 tabular-nums text-muted-foreground">
                        {groupGoalTotal}
                      </td>
                      <td className="text-right py-2 px-2 tabular-nums text-muted-foreground">
                        {Math.max(0, groupGoalTotal - groupTotal)}
                      </td>
                      <td
                        className={cn(
                          "text-right py-2 px-2 tabular-nums",
                          GOAL_TEXT[goalStatus(groupProjectedTotal, groupGoalTotal)],
                        )}
                      >
                        {groupProjectedTotal}
                      </td>
                    </tr>
                    {isDigitalGroup && (
                      <tr className="text-muted-foreground">
                        <td className="py-1 pr-4 sticky left-0 bg-card text-xs">
                          Section Monthly Goal
                        </td>
                        {months.map((month) => (
                          <td key={month} className="text-right py-1 px-2 tabular-nums text-xs">
                            {groupMonthlyGoalTotal.toFixed(1)}
                          </td>
                        ))}
                        <td colSpan={4} />
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Card>
        <CardHeader>
          <CardTitle>Total Leads — Faithful Platform ({fiscalYearLabel})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground sticky left-0 bg-card">
                    &nbsp;
                  </th>
                  {months.map((month) => (
                    <th
                      key={month}
                      className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap"
                    >
                      {formatMonth(month)}
                    </th>
                  ))}
                  <th className="text-right py-2 px-2 font-semibold whitespace-nowrap">Total</th>
                  <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                    Goal
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                    Remaining
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                    Projected
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b font-semibold">
                  <td className="py-2 pr-4 sticky left-0 bg-card">Total</td>
                  {monthTotals.map((total, i) => (
                    <td key={months[i]} className="text-right py-2 px-2 tabular-nums">
                      {total}
                    </td>
                  ))}
                  <td className="text-right py-2 px-2 tabular-nums">{grandTotal}</td>
                  <td className="text-right py-2 px-2 tabular-nums text-muted-foreground">
                    {goalTotal}
                  </td>
                  <td className="text-right py-2 px-2 tabular-nums text-muted-foreground">
                    {Math.max(0, goalTotal - grandTotal)}
                  </td>
                  <td
                    className={cn(
                      "text-right py-2 px-2 tabular-nums",
                      GOAL_TEXT[goalStatus(grandProjected, goalTotal)],
                    )}
                  >
                    {grandProjected}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="text-muted-foreground">
                  <td className="py-2 pr-4 sticky left-0 bg-card whitespace-nowrap">
                    {priorYearLabel} (Last Year)
                  </td>
                  {priorYearTotals.map((total, i) => (
                    <td key={months[i]} className="text-right py-2 px-2 tabular-nums">
                      {total}
                    </td>
                  ))}
                  <td className="text-right py-2 px-2 tabular-nums font-semibold">
                    {priorYearTotal}
                  </td>
                  <td colSpan={3} className="py-2 px-2 text-xs">
                    <span
                      className={cn(
                        "font-semibold tabular-nums",
                        yoyChange >= 0 ? "text-emerald-600" : "text-amber-600",
                      )}
                    >
                      {yoyChange >= 0 ? "+" : ""}
                      {(yoyChange * 100).toFixed(1)}%
                    </span>{" "}
                    vs. same {monthsElapsed === 1 ? "month" : `${monthsElapsed} months`} last year (
                    {priorYearToDateTotal})
                  </td>
                </tr>
                <tr className="text-muted-foreground">
                  <td className="py-2 pr-4 sticky left-0 bg-card whitespace-nowrap">
                    Growth Percentage
                  </td>
                  {monthTotals.map((total, i) => {
                    const prior = priorYearTotals[i] ?? 0;
                    const hasGrowth = prior > 0 && total > 0;
                    const growth = prior > 0 ? (total - prior) / prior : 0;
                    return (
                      <td
                        key={months[i]}
                        className={cn(
                          "text-right py-2 px-2 tabular-nums text-xs font-semibold",
                          !hasGrowth
                            ? "text-muted-foreground"
                            : growth >= 0
                              ? "text-emerald-600"
                              : "text-amber-600",
                        )}
                      >
                        {hasGrowth ? `${growth >= 0 ? "+" : ""}${(growth * 100).toFixed(1)}%` : "—"}
                      </td>
                    );
                  })}
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
